import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Calendar, Users, Clock, Plus, ChevronLeft, ChevronRight, Wrench, Building } from 'lucide-react';

function MaintenanceDutyRoster() {
  const { API } = useContext(AppContext);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('2025-02-01');
  const [activeTab, setActiveTab] = useState('Company');

  useEffect(() => {
    fetchRosterData();
  }, [selectedDate]);

  const fetchRosterData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/maintenance/roster`, {
        params: { date: selectedDate },
        
      });
      setRoster(response.data);
    } catch (error) {
      console.error('Error fetching roster:', error);
    } finally {
      setLoading(false);
    }
  };

  const getShiftColor = (shift) => {
    switch (shift) {
      case 'Day': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Night': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Duty': return 'bg-green-100 text-green-700';
      case 'Scheduled': return 'bg-blue-100 text-blue-700';
      case 'Off': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredRoster = roster.filter(r => r.employeeType === activeTab);

  const stats = {
    totalCompany: roster.filter(r => r.employeeType === 'Company').length,
    totalContractor: roster.filter(r => r.employeeType === 'Contractor').length,
    dayShift: roster.filter(r => r.shift === 'Day').length,
    nightShift: roster.filter(r => r.shift === 'Night').length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading duty roster...</div>;
  }

  return (
    <div className="space-y-6" data-testid="maintenance-duty-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Duty Roster</h1>
          <p className="text-gray-500 mt-1">Manage maintenance team shift schedules (Day & Night)</p>
        </div>
        <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
          <Plus size={18} />
          <span>Add Assignment</span>
        </button>
      </div>

      {/* Date Navigation */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex items-center space-x-2">
              <Calendar className="text-[#1a237e]" size={20} />
              <span className="text-lg font-semibold text-gray-900">
                {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center space-x-2">
            <Users className="text-blue-500" size={18} />
            <p className="text-sm text-blue-600 font-medium">Company Staff</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{stats.totalCompany}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center space-x-2">
            <Building className="text-purple-500" size={18} />
            <p className="text-sm text-purple-600 font-medium">Contractors</p>
          </div>
          <p className="text-2xl font-bold text-purple-700">{stats.totalContractor}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200">
          <p className="text-xs text-amber-600 font-medium">Day Shift (6AM-6PM)</p>
          <p className="text-xl font-bold text-amber-700">{stats.dayShift}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-4 border border-indigo-200">
          <p className="text-xs text-indigo-600 font-medium">Night Shift (6PM-6AM)</p>
          <p className="text-xl font-bold text-indigo-700">{stats.nightShift}</p>
        </div>
      </div>

      {/* Employee Type Tabs */}
      <div className="flex items-center space-x-2 bg-white rounded-xl p-1.5 border border-gray-200 w-fit">
        <button
          onClick={() => setActiveTab('Company')}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
            activeTab === 'Company'
              ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Users size={16} />
          <span>Company Employees</span>
        </button>
        <button
          onClick={() => setActiveTab('Contractor')}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
            activeTab === 'Contractor'
              ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Building size={16} />
          <span>Contractor Employees</span>
        </button>
      </div>

      {/* Shift Tables */}
      {['Day', 'Night'].map((shift) => (
        <div key={shift} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className={`px-4 py-3 border-b ${shift === 'Day' ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-gradient-to-r from-indigo-50 to-violet-50'}`}>
            <h3 className={`text-sm font-semibold flex items-center space-x-2 ${shift === 'Day' ? 'text-amber-700' : 'text-indigo-700'}`}>
              <Clock size={16} />
              <span>{shift} Shift ({shift === 'Day' ? '6:00 AM - 6:00 PM' : '6:00 PM - 6:00 AM'})</span>
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {activeTab === 'Company' ? (
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Employee ID</th>
                  ) : (
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Contractor</th>
                  )}
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Area</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRoster.filter(r => r.shift === shift).map((person) => (
                  <tr key={person.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 font-mono text-sm text-[#1a237e]">
                      {activeTab === 'Company' ? person.employeeId : person.contractorCompany}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{person.employeeName}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{person.role}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{person.area}</td>
                    <td className="px-4 py-4 text-sm font-mono text-gray-600">{person.phone}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(person.status)}`}>
                        {person.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredRoster.filter(r => r.shift === shift).length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No {activeTab.toLowerCase()} employees scheduled for {shift.toLowerCase()} shift
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MaintenanceDutyRoster;
